import type { LabelTargetType } from '@ipfabric/utils';
import type {
  LabelAssignmentTargetSearchResult,
  SelectedAssignmentTarget,
} from '../../assignedLabelsTypes';
import { formatTargetOptionLabel } from '../../targetIdentity';
import {
  applyInterfaceFilter,
  groupInterfaceTargetsByDevice,
} from './groupAssignmentTargets';

export type TargetDropdownOptionKind = LabelTargetType;

export type TargetDropdownOption = {
  badgeLabel: 'Device' | 'Interface';
  dataTestId?: string;
  indented?: boolean;
  key: string;
  kind: TargetDropdownOptionKind;
  label: string;
  primaryText: string;
  secondaryText?: string;
  selectable: boolean;
  target?: SelectedAssignmentTarget;
};

const toInterfaceTestIdSuffix = (interfaceName: string): string =>
  interfaceName.replace(/[/\\:]/g, '-');

const collectDevices = (
  results: LabelAssignmentTargetSearchResult[],
): Map<string, { hostname: string | null; sn: string }> => {
  const devices = new Map<string, { hostname: string | null; sn: string }>();

  for (const result of results) {
    if (result.type === 'device') {
      devices.set(result.sn, { sn: result.sn, hostname: result.hostname });
      continue;
    }

    if (!devices.has(result.sn)) {
      devices.set(result.sn, {
        sn: result.sn,
        hostname: result.hostname,
      });
    }
  }

  return devices;
};

export const buildTargetDropdownOptions = (
  results: LabelAssignmentTargetSearchResult[],
  showInterfaces: boolean,
  interfaceFilter: string,
): TargetDropdownOption[] => {
  const devices = collectDevices(results);
  const interfaceResults = results.filter(
    (result): result is Extract<typeof result, { type: 'intL2' }> =>
      result.type === 'intL2',
  );
  const interfaceGroups = applyInterfaceFilter(
    groupInterfaceTargetsByDevice(interfaceResults),
    interfaceFilter,
  );
  const interfacesByDeviceSn = new Map(
    interfaceGroups.map((group) => [group.deviceSn, group.interfaces]),
  );

  const visibleDevices = showInterfaces
    ? [...devices.values()]
    : results
        .filter(
          (result): result is Extract<typeof result, { type: 'device' }> =>
            result.type === 'device',
        )
        .map((device) => ({
          sn: device.sn,
          hostname: device.hostname,
        }));

  const options: TargetDropdownOption[] = [];

  for (const device of visibleDevices) {
    options.push({
      badgeLabel: 'Device',
      dataTestId: `assign-labels-target-option-${device.sn}`,
      key: `device:${device.sn}`,
      kind: 'device',
      label: formatTargetOptionLabel(device.sn, device.hostname),
      primaryText: device.sn,
      secondaryText: device.hostname?.trim() ? device.hostname : undefined,
      selectable: true,
      target: {
        type: 'device',
        sn: device.sn,
        hostname: device.hostname,
      },
    });

    if (!showInterfaces) {
      continue;
    }

    for (const iface of interfacesByDeviceSn.get(device.sn) ?? []) {
      options.push({
        badgeLabel: 'Interface',
        dataTestId: `assign-labels-target-option-${device.sn}-${toInterfaceTestIdSuffix(iface.interfaceName)}`,
        indented: true,
        key: `intL2:${device.sn}:${iface.interfaceName}`,
        kind: 'intL2',
        label: iface.interfaceName,
        primaryText: iface.interfaceName,
        selectable: true,
        target: {
          type: 'intL2',
          sn: device.sn,
          interfaceName: iface.interfaceName,
          hostname: device.hostname,
        },
      });
    }
  }

  return options;
};
